import { LogLevels } from '@logz-pkg/enums';

export const yellowPaletteDark: Record<number, string> = {
  100: '#402c04',
  200: '#4b3404',
  300: '#614004',
  400: '#764f06',
  500: '#8c5c07',
  600: '#f9cd7d',
  700: '#fada9d',
  800: '#fce6be',
  900: '#fdf3de',
  1000: '#fef9ef',
};

export const royalBluePaletteDark: Record<number, string> = {
  100: '#282b2d',
  200: '#323d42',
  300: '#3e4c53',
  400: '#4a5b63',
  500: '#566b75',
  600: '#8097a1',
  700: '#99abb3',
  800: '#b3c0c6',
  900: '#ccd5d9',
  1000: '#e6eaec',
};

export const bluePaletteDark: Record<number, string> = {
  100: '#212425',
  200: '#292c2e',
  300: '#373b3e',
  400: '#2f4363',
  500: '#385077',
  600: '#849dc5',
  700: '#a3b6d3',
  800: '#c1cee2',
  900: '#e0e7f0',
  1000: '#f0f3f8',
};

export const grayPaletteDark: Record<number, string> = {
  0: '#1a1d1e',
  100: '#202223',
  200: '#242829',
  300: '#2a2d2e',
  400: '#343739',
  500: '#4c5356',
  600: '#afafaf',
  700: '#d7d7d7',
  800: '#e7e7e7',
  900: '#efefef',
  1000: '#f7f7f7',
};

export const redPaletteDark: Record<number, string> = {
  100: '#4d1708',
  200: '#59190b',
  300: '#661e0c',
  400: '#81270e',
  500: '#9b2d12',
  600: '#ed7d61',
  700: '#f29e88',
  800: '#f6beb0',
  900: '#f8cec4',
  1000: '#fbdfd7',
};

export const greenPaletteDark: Record<number, string> = {
  100: '#1f3817',
  200: '#1d4a2a',
  300: '#225b33',
  400: '#2a6c3d',
  500: '#307c45',
  600: '#80ce83',
  700: '#99d89c',
  800: '#b3e2b5',
  900: '#ccebcd',
  1000: '#e6f5e6',
};

export const orangePaletteDark: Record<number, string> = {
  100: '#383807',
  200: 'transparent',
  300: 'transparent',
  400: '#d86d00',
  500: '#ad671d',
  600: '#c47621',
  700: '#f57c00',
  800: 'transparent',
  900: 'transparent',
  1000: '#fefef6',
};

export const magentaPaletteDark: Record<number, string> = {
  100: 'transparent',
  200: 'transparent',
  300: 'transparent',
  400: '#812e77',
  500: '#914286',
  600: '#a54b99',
  700: '#d07ac5',
  800: 'transparent',
  900: 'transparent',
  1000: 'transparent',
};

export const productPaletteDark = {
  alerts: '#652d78',
  alerts_dark: '#482953',
  alerts_light: '#482953',

  insights: '#2f4f87',
  insights_dark: '#061127',
  insights_light: '#393e40',

  exceptions: '#2d7862',
  exceptions_dark: '#1e5042',
  exceptions_light: '#1e5042',

  logs: '#165971',
  metrics: '#992c11',
  tracing: '#a06b07',
};

export const statusPaletteDark = {
  running: '#3f795c',
  succeeded: '#3f795c',
  pending: '#6e8102',
  unknown: '#3b4143',
  failed: '#9e3e3e',
  low: '#b79100',
  medium: '#acc800',
  high: '#c87800',
  critical: '#9d1d1d',
  good: '#086738',
};

export const colorsDark = {
  yellow: yellowPaletteDark,
  green: greenPaletteDark,
  blue: bluePaletteDark,
  royalBlue: royalBluePaletteDark,
  red: redPaletteDark,
  gray: grayPaletteDark,
  orange: orangePaletteDark,
  magenta: magentaPaletteDark,
  product: productPaletteDark,
  status: statusPaletteDark,
};

export type ColorDark = [
  'royalBlue.100',
  'royalBlue.200',
  'royalBlue.300',
  'royalBlue.400',
  'royalBlue.500',
  'royalBlue.600',
  'royalBlue.700',
  'royalBlue.800',
  'royalBlue.900',
  'royalBlue.1000',
  'blue.100',
  'blue.200',
  'blue.300',
  'blue.400',
  'blue.500',
  'blue.600',
  'blue.700',
  'blue.800',
  'blue.900',
  'blue.1000',
  'yellow.100',
  'yellow.200',
  'yellow.300',
  'yellow.400',
  'yellow.500',
  'yellow.600',
  'yellow.700',
  'yellow.800',
  'yellow.900',
  'yellow.1000',
  'red.100',
  'red.200',
  'red.300',
  'red.400',
  'red.500',
  'red.600',
  'red.700',
  'red.800',
  'red.900',
  'red.1000',
  'green.100',
  'green.200',
  'green.300',
  'green.400',
  'green.500',
  'green.600',
  'green.700',
  'green.800',
  'green.900',
  'green.1000',
  'gray.0',
  'gray.100',
  'gray.200',
  'gray.300',
  'gray.400',
  'gray.500',
  'gray.600',
  'gray.700',
  'gray.800',
  'gray.900',
  'gray.1000',
  'orange.100',
  'orange.200',
  'orange.300',
  'orange.400',
  'orange.500',
  'orange.600',
  'orange.700',
  'orange.800',
  'orange.900',
  'orange.1000',
  'magenta.100',
  'magenta.200',
  'magenta.300',
  'magenta.400',
  'magenta.500',
  'magenta.600',
  'magenta.700',
  'magenta.800',
  'magenta.900',
  'magenta.1000',
  'product.alerts',
  'product.alerts_dark',
  'product.alerts_light',
  'product.insights',
  'product.insights_dark',
  'product.insights_light',
  'product.exceptions',
  'product.exceptions_dark',
  'product.exceptions_light',
  'product.logs',
  'product.metrics',
  'product.tracing',
  'status.running',
  'status.succeeded',
  'status.pending',
  'status.unknown',
  'status.failed',
  'status.low',
  'status.medium',
  'status.high',
  'status.critical',
  'status.good',
][number];

export const logLevelsColorsDark: Record<LogLevels, string> = {
  INFO: bluePaletteDark['700'],
  ERROR: redPaletteDark['700'],
  WARN: yellowPaletteDark['700'],
  DEBUG: bluePaletteDark['500'],
  UNKNOWN: grayPaletteDark['500'],
};
